import { useStickToBottomContext } from '@crowdex/ui/ai/conversation';
import { Tool } from '@crowdex/ui/ai/tool';
import type { ComponentProps } from 'react';
import { useCallback, useRef } from 'react';

/**
 * A `Tool` disclosure that keeps the conversation scroll position pinned while
 * it expands or collapses. The stick-to-bottom container otherwise animates the
 * viewport toward the growing content, which reads as a jump when the user is
 * deliberately opening a tool higher up the transcript.
 */
export function StableTool({
  className,
  onKeyDownCapture,
  onOpenChange,
  onPointerDownCapture,
  ...props
}: ComponentProps<typeof Tool>) {
  const { state, stopScroll } = useStickToBottomContext();
  const scrollTopBeforeInteraction = useRef<number | null>(null);

  const preserveScroll = useCallback(
    (open: boolean) => {
      const scrollTop = scrollTopBeforeInteraction.current ?? state.scrollTop;
      scrollTopBeforeInteraction.current = null;

      stopScroll();
      state.animation = undefined;
      state.velocity = 0;
      state.accumulated = 0;
      onOpenChange?.(open);

      requestAnimationFrame(() => {
        stopScroll();
        state.animation = undefined;
        state.scrollTop = scrollTop;
        requestAnimationFrame(() => {
          stopScroll();
          state.animation = undefined;
          state.scrollTop = scrollTop;
        });
      });
    },
    [onOpenChange, state, stopScroll],
  );

  return (
    <Tool
      {...props}
      className={className === undefined ? 'desktop-tool-enter' : `desktop-tool-enter ${className}`}
      onKeyDownCapture={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          scrollTopBeforeInteraction.current = state.scrollTop;
        }
        onKeyDownCapture?.(event);
      }}
      onOpenChange={preserveScroll}
      onPointerDownCapture={(event) => {
        scrollTopBeforeInteraction.current = state.scrollTop;
        onPointerDownCapture?.(event);
      }}
    />
  );
}
